import { useQuery } from "@tanstack/react-query";
import type { PortalDocument, PortalDocumentDetail } from "@wms/domain";
import { statusLabel } from "@wms/domain";
import { Alert, Drawer, Segmented, Steps, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { portalApi } from "../api/portal";
import { errorMessage } from "../api/client";
import { formatDate, formatDateTime, formatQty } from "../api/format";
import { linesProgressText, unitProgressText, unitTotalsText } from "./portalText";

/** C03: own receipts and orders with the process ribbon — done, current, next — and the plan / fact per line. */
export function PortalOperationsPage() {
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState<"receipts" | "orders">(() => (params.get("kind") === "orders" ? "orders" : "receipts"));
  const [openId, setOpenId] = useState<string | null>(null);
  // ТЗ «Рабочие столы» 31.10.2026: «Последние операции» of the desktop open exactly this document of the cabinet
  useEffect(() => {
    const wanted = params.get("open");
    if (!wanted) return;
    setKind(params.get("kind") === "orders" ? "orders" : "receipts");
    setOpenId(wanted);
    params.delete("open");
    params.delete("kind");
    setParams(params, { replace: true });
  }, [params, setParams]);
  const list = useQuery({ queryKey: ["portal", "operations", kind], queryFn: () => portalApi.get<PortalDocument[]>(`/operations/${kind}`) });
  const detail = useQuery({ queryKey: ["portal", "operation", kind, openId], queryFn: () => portalApi.get<PortalDocumentDetail>(`/operations/${kind}/${openId}`), enabled: !!openId });
  // CW31-B02: the totals per unit («160 шт · 2,5 кг», «1 кг из 2,5 кг; 0 шт из 160 шт») and the lines done — pieces
  // and kilograms are never one number; the plain sum is shown only by a server that does not give the units
  const columns = [
    { title: "Номер", dataIndex: "number", render: (v: string, r: PortalDocument) => <a onClick={() => setOpenId(r.id)}>{v}</a> },
    { title: "Дата", dataIndex: "date", render: (v: string) => formatDate(v) },
    { title: "Плановое время", dataIndex: "planned_at", render: (v: string | null) => (v ? formatDateTime(v) : "") },
    { title: "Состояние", dataIndex: "status", render: (v: string) => <Tag>{statusLabel(v)}</Tag> },
    {
      title: kind === "receipts" ? "Ожидаем" : "Заказано",
      dataIndex: "quantity_plan",
      align: "right" as const,
      render: (v: unknown, r: PortalDocument) => (r.totals_by_unit ? unitTotalsText(r.totals_by_unit, "plan") : formatQty(v)),
    },
    {
      title: kind === "receipts" ? "Принято" : "Отгружено",
      dataIndex: "quantity_fact",
      align: "right" as const,
      render: (v: unknown, r: PortalDocument) =>
        r.totals_by_unit ? (
          <span data-testid={`portal-operation-fact-${r.number}`}>
            {unitProgressText(r.totals_by_unit)}
            {r.lines_total ? (
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                {linesProgressText(r.lines_done, r.lines_total)}
              </Typography.Text>
            ) : null}
          </span>
        ) : (
          formatQty(v)
        ),
    },
  ];
  const d = detail.data;
  return (
    <div>
      <div className="wms-page-header">
        <div>
          <h1>Операции</h1>
          <div className="wms-subtitle">Что склад делает с вашим товаром: этапы, время, количество по плану и по факту</div>
        </div>
        <Segmented value={kind} onChange={(v) => setKind(v as "receipts" | "orders")} options={[{ value: "receipts", label: "Поступления" }, { value: "orders", label: "Заказы на отгрузку" }]} />
      </div>
      {list.isError && <Alert type="error" showIcon message={errorMessage(list.error)} style={{ marginBottom: 12 }} />}
      <Table<PortalDocument> rowKey="id" size="middle" loading={list.isLoading} dataSource={list.data ?? []} columns={columns} pagination={{ pageSize: 25 }} scroll={{ x: true }} locale={{ emptyText: "Документов пока нет" }} />
      <Drawer open={!!openId} onClose={() => setOpenId(null)} width={720} title={d ? `${kind === "receipts" ? "Поступление" : "Заказ"} ${d.number}` : "Документ"}>
        {detail.isError && <Alert type="error" showIcon message={errorMessage(detail.error)} />}
        {d && (
          <>
            {d.request && (
              <Typography.Paragraph>
                По заявке <b>{d.request.number}</b>
              </Typography.Paragraph>
            )}
            {d.cancelled && <Alert type="warning" showIcon message="Документ отменён складом" style={{ marginBottom: 12 }} />}
            <Steps
              size="small"
              direction="vertical"
              items={d.timeline.map((step) => ({
                title: step.label,
                description: step.at ? formatDateTime(step.at) : step.state === "next" ? "далее" : undefined,
                status: step.state === "done" ? "finish" : step.state === "current" ? "process" : step.state === "skipped" ? "error" : "wait",
              }))}
              style={{ marginBottom: 16 }}
            />
            <Table
              rowKey="line_no"
              size="small"
              pagination={false}
              dataSource={d.lines}
              columns={[
                { title: "№", dataIndex: "line_no", width: 50 },
                { title: "Товар", dataIndex: ["product", "name"], render: (_: unknown, r: PortalDocumentDetail["lines"][number]) => `${r.product.code} — ${r.product.name}` },
                { title: "Ед.", dataIndex: "unit" },
                { title: kind === "receipts" ? "Ожидаем" : "Заказано", dataIndex: "quantity_plan", align: "right" as const, render: (v: unknown) => formatQty(v) },
                ...(kind === "orders" ? [{ title: "Собрано", dataIndex: "quantity_picked", align: "right" as const, render: (v: unknown) => formatQty(v) }] : []),
                { title: kind === "receipts" ? "Принято" : "Отгружено", dataIndex: "quantity_fact", align: "right" as const, render: (v: unknown) => formatQty(v) },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  );
}
